import React, { useCallback, useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import { useDispatch } from "react-redux"
import { Button, Col, Row, notification } from "antd"
import {
	ContentWrapper,
	GoogleMaps,
	Icon,
	ZoneModal,
	ZoneDeleteModal,
} from "components"
import { ZonePayload } from "components/ZoneModal"
import { geojsonToPolygonPaths } from "utils/geojson"
import { API, Endpoints } from "utils/api"
import { setBreadcrumb, setLoader } from "store/actions"
import Strings from "utils/strings"

/**
 * Global GeoJSON zones: zones with no owning contract, reusable by all of them.
 * Admin-only (see the route's role gate); the same create/edit/delete modal the
 * contract page uses.
 */
const GeojsonZones: React.FC = () => {
	const dispatch = useDispatch()

	const [zones, setZones] = useState<any[]>([])
	const [showModal, setShowModal] = useState(false)
	const [zoneToEdit, setZoneToEdit] = useState<any | null>(null)
	const [zoneToDelete, setZoneToDelete] = useState<any | null>(null)
	const [deleteError, setDeleteError] = useState<string | null>(null)

	// No contractId means the API returns the global zones alone.
	const fetchZones = useCallback(async () => {
		dispatch(setLoader(true))
		try {
			const response = await API.get({ url: Endpoints.uriGeojsonZones() })
			if (response.ok) {
				setZones(response.data.results || [])
			} else {
				notification.error({
					message: Strings.geojsonZones.title,
					description: response.data?.message || Strings.geojsonZones.loadError,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.geojsonZones.title,
				description: Strings.geojsonZones.loadError,
				placement: "bottomRight",
				duration: 5,
			})
		}
		dispatch(setLoader(false))
	}, [dispatch])

	useEffect(() => {
		fetchZones()
	}, [fetchZones])

	useEffect(() => {
		dispatch(
			setBreadcrumb(() => ({
				locations: [
					{
						text: Strings.sidebar.settings,
						route: "/settings",
						icon: "preferences",
					},
					{ text: Strings.geojsonZones.title, icon: "location" },
				],
			}))
		)

		return () => {
			dispatch(setBreadcrumb(null))
		}
	}, [dispatch])

	const handleSaveZone = async (payload: ZonePayload): Promise<boolean> => {
		const editingId = zoneToEdit?._id
		try {
			// Omitting `contract` is what makes the zone global.
			const response = editingId
				? await API.put({
						url: Endpoints.uriGeojsonZones(editingId),
						data: payload,
					})
				: await API.post({
						url: Endpoints.uriGeojsonZones(),
						data: payload,
					})

			if (response.ok) {
				const zone = response.data.results
				setZones((prev) =>
					editingId
						? prev.map((z: any) => (z._id === editingId ? zone : z))
						: [zone, ...prev]
				)
				notification.success({
					message: Strings.geojsonZones.title,
					description: editingId
						? Strings.contracts.geojsonUpdated
						: Strings.contracts.geojsonCreated,
					placement: "bottomRight",
					duration: 5,
				})
				return true
			}
			notification.error({
				message: Strings.geojsonZones.title,
				description: response.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
			return false
		} catch (err) {
			notification.error({
				message: Strings.geojsonZones.title,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
			return false
		}
	}

	// The API refuses to delete a zone contracts still reference, and names them;
	// that refusal is shown inside the confirmation modal.
	const handleDeleteZone = async () => {
		if (!zoneToDelete) return
		try {
			const response = await API.delete({
				url: Endpoints.uriGeojsonZones(zoneToDelete._id),
			})
			if (response.ok) {
				setZones((prev) => prev.filter((z: any) => z._id !== zoneToDelete._id))
				setZoneToDelete(null)
				setDeleteError(null)
				notification.success({
					message: Strings.geojsonZones.title,
					description: Strings.geojsonZones.deleted,
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				setDeleteError(response.data?.message || Strings.serverErrors.wentWrong)
			}
		} catch (err) {
			setDeleteError(Strings.serverErrors.wentWrong)
		}
	}

	const renderZone = (zone: any) => {
		const polygons = geojsonToPolygonPaths(zone.geojson).map((paths) => ({
			paths,
			fillColor: "#722ED1",
			fillOpacity: 0.15,
			strokeColor: "#722ED1",
			strokeOpacity: 1,
			strokeWeight: 2.5,
		}))

		const allPoints = polygons.flatMap((p) => p.paths)
		const boundingBox = allPoints.length
			? {
					south: Math.min(...allPoints.map((pt) => pt.lat)),
					north: Math.max(...allPoints.map((pt) => pt.lat)),
					west: Math.min(...allPoints.map((pt) => pt.lng)),
					east: Math.max(...allPoints.map((pt) => pt.lng)),
				}
			: undefined

		return (
			<Col xs={24} sm={12} lg={8} key={zone._id}>
				{/* Column layout so the map fills whatever height the tallest card in
				    the row sets, keeping the action row pinned to the bottom. */}
				<div
					style={{
						border: "1px solid #eee",
						borderRadius: 6,
						padding: 12,
						height: "100%",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<div style={{ marginBottom: 8 }}>
						<strong>{zone.name}</strong>
						<span style={{ color: "#888", marginLeft: 8 }}>
							{Strings.geojsonZones.buffer}: {zone.buffer ?? 0}km
						</span>
						<p style={{ color: "#888", margin: "4px 0 0" }}>
							{zone.description || Strings.geojsonZones.noDescription}
						</p>
					</div>

					{polygons.length > 0 && (
						<div style={{ flex: 1, minHeight: 300 }}>
							<GoogleMaps
								polygons={polygons}
								boundingBox={boundingBox}
								mapContainerStyle={{
									width: "100%",
									height: "100%",
									minHeight: "300px",
									borderRadius: 5,
								}}
							/>
						</div>
					)}

					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							marginTop: 8,
						}}
					>
						<Button
							type="text"
							onClick={() => {
								setZoneToEdit(zone)
								setShowModal(true)
							}}
						>
							{Strings.contracts.geojsonEdit}
						</Button>
						<Button
							type="text"
							danger
							onClick={() => {
								setDeleteError(null)
								setZoneToDelete(zone)
							}}
						>
							{Strings.contracts.geojsonRemove}
						</Button>
					</div>
				</div>
			</Col>
		)
	}

	return (
		<>
			<Helmet>
				<title>{Strings.geojsonZones.title}</title>
			</Helmet>

			<ContentWrapper>
				<div style={{ marginBottom: 10 }} className="MultiTitleSeparator">
					<h2>{Strings.geojsonZones.title}</h2>
					<button
						onClick={() => {
							setZoneToEdit(null)
							setShowModal(true)
						}}
					>
						<Icon name="plus" />
					</button>
				</div>

				<p style={{ color: "#888" }}>{Strings.geojsonZones.subtitle}</p>

				{zones.length === 0 ? (
					<p style={{ color: "#888" }}>{Strings.geojsonZones.empty}</p>
				) : (
					<Row gutter={[16, 16]}>{zones.map(renderZone)}</Row>
				)}
			</ContentWrapper>

			<ZoneModal
				visible={showModal}
				zone={zoneToEdit}
				onClose={() => {
					setShowModal(false)
					setZoneToEdit(null)
				}}
				onSubmit={handleSaveZone}
			/>

			<ZoneDeleteModal
				visible={!!zoneToDelete}
				zone={zoneToDelete}
				inUse={false}
				error={deleteError}
				onClose={() => {
					setZoneToDelete(null)
					setDeleteError(null)
				}}
				onConfirm={handleDeleteZone}
			/>
		</>
	)
}

export default GeojsonZones
